import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { decodeProcessItemContent, type LabelListContent } from "../../../api/content/authoring/processContentDecoders";
import { commitItemEdits } from "../../../api/content/changeSetsApi";
import type { ItemVersion } from "../../../api/content/itemsApi";
import type { ItemChange, Review } from "../../../api/content/reviewDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useItemVersionPair } from "../../items/changes/useItemVersionPair";
import { useSubmission } from "../../../shared/state/useSubmission";
import { parseDocumentVersionRef } from "../../../shared/references/contentReferences";
import AppButton from "../../../ui/buttons/AppButton";
import { iriLocalName } from "../../../shared/references/shortReferences";
import { brokenLinks, labelListDiff, relinkedDefinition, type BrokenLink, type LabelField } from "./labelListDiff";
import ChangeRow from "../../../ui/content/ChangeRow";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import EmptyState from "../../../ui/feedback/EmptyState";
import { useReviewTermNames } from "../conflicts/conflictTermNames";

function labelList(version: ItemVersion | null): LabelListContent | null {
  const content = version === null ? null : decodeProcessItemContent("label_list", version.definition);
  return content?.kind === "label_list" ? content.definition : null;
}

/**
 * A changed label list in review (curation-ui.md CU-4), in three blocks. Labels: added,
 * removed and changed, by key. Needs a decision: links to terms the change set removes or
 * renames; re-targeting or dropping one is an edit, committed to the change set on the
 * head shown, and the label list's curator approves the result. Rules: this list is
 * written by hand, so there is no rule set to be in step with.
 */
export default function LabelListUnit({ item, review, canEdit, onChanged }: { item: ItemChange; review: Review; canEdit: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const diffHeadingId = useId();
  const decideHeadingId = useId();
  const rulesHeadingId = useId();
  const { state, reload } = useItemVersionPair(item);
  const versions = state.status === "loaded" ? state.value : null;
  const before = labelList(versions?.before ?? null);
  const after = labelList(versions?.after ?? null);
  const diff = useMemo(() => labelListDiff(before, after), [before, after]);
  const links = useMemo(() => brokenLinks(after, item.itemId, review.conflictCheck?.findings ?? []), [after, item.itemId, review.conflictCheck]);
  const labels = useReviewTermNames(review);
  const termName = (iri: string) => labels[iri] ?? iriLocalName(iri);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("reviews.item.loadingDefinition")} onRetry={reload} compact />;
  const stored = versions?.after?.definition ?? null;
  return (
    <div className="space-y-4">
      <section className="space-y-2" aria-labelledby={diffHeadingId}>
        <h4 id={diffHeadingId} className="app-subsection-title text-sm">
          {t("reviews.collaboration.labels.labels")}
        </h4>
        {diff.added.length + diff.removed.length + diff.changed.length === 0 ? <p className="app-text-muted text-sm">{t("reviews.collaboration.labels.noLabelChange")}</p> : null}
        <ul className="space-y-1 text-sm">
          {diff.added.map((label) => (
            <ChangeRow key={`added:${label.key}`} kind="added" className="py-1">
              <span className="font-medium">{t("reviews.collaboration.labels.added", { name: label.name })}</span> <span className="app-text-muted text-xs">{label.description}</span>
            </ChangeRow>
          ))}
          {diff.removed.map((label) => (
            <ChangeRow key={`removed:${label.key}`} kind="removed" className="py-1">
              <span className="font-medium">{t("reviews.collaboration.labels.removed", { name: label.name })}</span>
            </ChangeRow>
          ))}
          {diff.changed.map((change) => (
            <ChangeRow key={`changed:${change.after.key}`} kind="changed" className="py-1">
              <p className="font-medium">{t("reviews.collaboration.labels.changed", { name: change.after.name })}</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 text-xs">
                {change.fields.map((field) => (
                  <div key={field} className="contents">
                    <dt className="app-text-muted">{t(`reviews.collaboration.labels.fields.${field}`)}</dt>
                    <dd>
                      <span className="line-through opacity-75">{show(change.before[field], field, termName)}</span> <span className="font-medium">{show(change.after[field], field, termName)}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </ChangeRow>
          ))}
        </ul>
        {diff.unchangedCount > 0 ? <p className="app-text-muted text-xs">{t("reviews.collaboration.labels.unchanged", { count: diff.unchangedCount })}</p> : null}
      </section>
      <section className="space-y-2" aria-labelledby={decideHeadingId}>
        <h4 id={decideHeadingId} className="app-subsection-title text-sm">
          {t("reviews.collaboration.labels.decide")}
        </h4>
        {links.length === 0 ? <EmptyState>{t("reviews.collaboration.labels.nothingToDecide")}</EmptyState> : null}
        <ul className="space-y-2">
          {links.map((link) => (
            <BrokenLinkRow
              key={`${link.labelKey}:${link.term}`}
              link={link}
              termName={termName}
              canEdit={canEdit && stored !== null && item.newReference !== null}
              commit={async (replacement) => {
                const basedOn = item.newReference === null ? null : parseDocumentVersionRef(item.newReference)?.version ?? null;
                if (stored === null || basedOn === null) {
                  return;
                }
                // The definition as stored, so fields the decoder leaves out (its lifecycle) stay.
                const content = relinkedDefinition(stored as unknown as LabelListContent, link, replacement);
                await commitItemEdits(review.changeSet.changeSetId, review.head, [{ operation: "replace", item_id: item.itemId, content, based_on: basedOn }]);
                onChanged();
              }}
            />
          ))}
        </ul>
      </section>
      <section className="space-y-1" aria-labelledby={rulesHeadingId}>
        <h4 id={rulesHeadingId} className="app-subsection-title text-sm">
          {t("reviews.collaboration.labels.rules")}
        </h4>
        <p className="app-text-muted text-sm">{t("reviews.collaboration.labels.byHand")}</p>
      </section>
    </div>
  );
}

/** A field's value in words; linked terms by their labels, everything else as written. */
function show(value: string | readonly string[], field: LabelField, termName: (iri: string) => string): string {
  if (typeof value === "string") {
    return value === "" ? "—" : value;
  }
  return value.length === 0 ? "—" : value.map((entry) => (field === "ontology_terms" ? termName(entry) : entry)).join(", ");
}

function BrokenLinkRow({ link, termName, canEdit, commit }: { link: BrokenLink; termName: (iri: string) => string; canEdit: boolean; commit: (replacement: string | null) => Promise<void> }) {
  const { t } = useTranslation();
  const editing = useSubmission();
  const run = (replacement: string | null) => {
    editing.submit(() => commit(replacement));
  };
  return (
    <li className="app-card space-y-1 p-2 text-sm">
      <p>
        {link.renamedTo === null
          ? t("reviews.collaboration.labels.linkRemoved", { label: link.labelName, term: termName(link.term) })
          : t("reviews.collaboration.labels.linkRenamed", { label: link.labelName, term: termName(link.term), renamedTo: termName(link.renamedTo) })}
      </p>
      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          {link.renamedTo !== null ? (
            <AppButton
              size="xs"
              disabled={editing.state.status === "submitting"}
              onClick={() => {
                run(link.renamedTo);
              }}
            >
              {t("reviews.collaboration.labels.retarget", { term: termName(link.renamedTo) })}
            </AppButton>
          ) : null}
          <AppButton
            size="xs"
            variant="ghost"
            disabled={editing.state.status === "submitting"}
            onClick={() => {
              run(null);
            }}
          >
            {t("reviews.collaboration.labels.dropLink")}
          </AppButton>
        </div>
      ) : null}
      {editing.state.status === "failed" ? <ErrorNotice error={editing.state.error} compact={true} /> : null}
    </li>
  );
}
